import Vue from 'vue'
import VueMask from 'v-mask'
import VModal from 'vue-js-modal'
import i18n from '../plugins/i18n'
import '../utils/event'
import {injectTokenInterceptor} from "../utils/tokenInterceptor";
import Toasted from "vue-toasted";
import dateFilter from '../utils/dateFilter'
import money from 'v-money';
import {API_URL} from "../utils/consts"
import '../utils/autobahn'

import VueForm from 'vue-form'
import {validateEmail} from '../Registration/validators/emailValidator'
import {validateTelephone} from '../Registration/validators/telephoneValidator'
import truncateFilter from '../utils/truncateFilter'
import {sleep} from '../utils/sleep'
import {formatPrice} from '../utils/priceFormatter'

Vue.use(VueForm, {
    validators: {
        email: value => validateEmail(value),
        phone: value => validateTelephone(value),
    }
})
Vue.use(VModal, {dynamic: true});
Vue.use(VueMask)
Vue.use(money)
Vue.use(Toasted)
Vue.filter('formatDate', dateFilter)

new Vue({
    i18n,
    el: '#monetization-detail',
    components: {
        'monetization-dialog': () => import(/* webpackPrefetch: true */'../MyAccount/components/Monetization/MonetizationDialog'),
        'join-to-tour-dialog': () => import(/* webpackPrefetch: true */'./components/JoinToTourDialog'),
        'expert-opinion-document-dialog': () => import(/* webpackPrefetch: true */'./components/ExpertOpinionDialog'),
        'monetization-documents': () => import(/* webpackPrefetch: true */'./components/MonetizationDocuments'),
        'skeleton': async () => {
            const {ContentLoader} = await import('vue-content-loader');
            return ContentLoader
        },
        'change-password-modal': () => import(/* webpackPrefetch: true */'../Modals/ChangePasswordModal')
    },

    data() {
        return {
            token: localStorage.getItem('accessToken') || '',
            monetizationId: null,
            monetizationType: null,
            monetizationStatus: null,
            monetizationArchive: false,
            tours: [],
            identificator: null,
            password: null,
            account: null,
            logRecords: [],
            isTenderOrDirectOrLease: false,
            needLoginForJoin: false,
            isOfferValidityExpired: false,
            showMonetizationComponent: false,
            showJoinToTourDialogComponent: false,
            moment: null,
            initialCurrentBid: 0
        }
    },

    computed: {
        isLogged() {
            return !!this.token
        },

        showRequestRegisteredButton() {
            if (!this.monetizationStatus) { return false }
            return this.isLogged && !this.monetizationStatus.allowToJoin
        },

        showValidityExpiredButton() {
            if (!this.monetizationStatus) { return false }
            return this.isOfferValidityExpired
        },

        showJoinToDirectSaleButton() {
            if (!this.monetizationStatus) {return false}
            if (this.monetizationStatus.finalState === 'abandoned') { return false }
            const {type, allowToJoin, isRequirementsFulfilled} = this.monetizationStatus
            return !this.isOfferValidityExpired && type === 'directSale' && allowToJoin && !isRequirementsFulfilled
        },

        showJoinToLeaseButton() {
            if (!this.monetizationStatus) {return false}
            if (this.monetizationStatus.finalState === 'abandoned') { return false }
            const {type, allowToJoin, isRequirementsFulfilled} = this.monetizationStatus
            return !this.isOfferValidityExpired && (type === 'lease') && allowToJoin && !isRequirementsFulfilled
        },

        showJoinToTenderButton() {
            if (!this.monetizationStatus) {return false}
            if (this.monetizationStatus.finalState === 'abandoned') { return false }
            const {type, allowToJoin, isRequirementsFulfilled} = this.monetizationStatus
            return !this.isOfferValidityExpired && (type === 'tender' || type === 'secretTender') && allowToJoin && !isRequirementsFulfilled
        },

        showEnterToAuctionButton() {
            if (!this.monetizationStatus) {return false}
            if (this.monetizationStatus.finalState === 'abandoned') {return false}
            if (this.monetizationStatus.place === 'stone') {return false}
            return this.isLogged && this.monetizationStatus && this.monetizationStatus.isRequirementsFulfilled
        },

        showJoinToAuctionButton() {
            if (!this.monetizationStatus) {return false}
            if (this.monetizationStatus.finalState === 'abandoned') { return false }
            const {allowToJoin, isRequirementsFulfilled} = this.monetizationStatus
            return this.isLogged && allowToJoin && !isRequirementsFulfilled
        },

        showUnauthorizedButton() {
            if (!this.monetizationStatus) { return false }
            const {allowToJoin, isRequirementsFulfilled} = this.monetizationStatus
            return this.isLogged && !allowToJoin && !isRequirementsFulfilled
        },

        showWillBeLaunchedButton() {
            if (!this.monetizationStatus) { return false }
            const {isRequirementsFulfilled, info} = this.monetizationStatus
            let auctionRunning = false
            if (info.start?.date && this.moment) {
                const today = this.moment(new Date())
                const dateToCompare = this.moment(info.start.date)
                auctionRunning = dateToCompare.startOf('day').isSame(today.startOf('day')) || dateToCompare.startOf('day').isBefore(today.startOf('day'))
            }
            return isRequirementsFulfilled && !auctionRunning
        },

        showNeedLoginButton() {
            return !this.isLogged
        },

        currentBid() {
            if (this.logRecords && this.logRecords.length) {
                return formatPrice(this.logRecords[0].value ?? this.initialCurrentBid)
            }
            return formatPrice(this.initialCurrentBid)
        }
    },

    async created() {
        const {default: mom} = await import('moment')
        this.moment = mom
        injectTokenInterceptor(this.$toasted, false)
        this.token = localStorage.getItem('accessToken') || null
        const el = document.getElementById('mapItems')
        const {id, type, archive} = JSON.parse(el.innerHTML)[0]
        const currentBidEl = document.getElementById('js-currentBid');
        if (currentBidEl) {
            try {
                this.initialCurrentBid = JSON.parse(el.innerHTML).value;
            } catch (e) {}
        }
        this.monetizationId = id
        this.monetizationType = type
        this.monetizationArchive = archive
        if (this.monetizationId) {
            await this.getTours()
            await this.getRequestStatus()
            if (this.token) {
                this.account = await this.getAccount()
                this.setAccountFullName()
            }
        }

        Event.listen('refreshMonetization', () => {
            this.getRequestStatus()
        })

        const translateEl = document.getElementById('translates-monetizationDetail')
        this.$i18n.setLocaleMessage('cs', JSON.parse(translateEl.innerHTML))
        this.fetchPublicLog()
        this.connectToWS()
    },

    methods: {
        async login() {
            const {identificator, password} = this
            if (identificator && password) {
                const data = {grant_type: 'client_credentials', client_id: identificator, client_secret: password}
                try {
                    const {default: axios} = await import('axios')
                    const response = await axios.post(`${API_URL}/token`, data, {headers: {'content-type': 'application/json'}})
                    this.setAccessToken(response.data.access_token)
                    if (this.token && this.monetizationId) {
                        await this.getRequestStatus()
                    }
                    if (this.monetizationStatus && this.monetizationStatus.isRequirementsFulfilled) {
                        window.location = `mistnost/${this.monetizationId}`
                    } else {
                        window.location.reload(true);
                    }
                } catch (e) {
                    const {ErrorModal} = await import(/* webpackPrefetch: true */'../Modals/ErrorModal')
                    if (e.response && e.response.data['errors'] && e.response.data['errors']['code'] === 1006) {
                        this.$modal.show('change-password-dialog')
                    } else {
                        ErrorModal.show(this, this.$i18n.t(e.response && e.response.data['error'] ? `error.${e.response.data['error']}` : 'error.somethingWrong'))
                    }
                }
            }
        },

        async getRequestStatus() {
            const isTenderOrDirectOrLease = (this.monetizationType === 'tender' || this.monetizationType === 'secretTender' || this.monetizationType === 'directSale' || this.monetizationType === 'lease')
            this.isTenderOrDirectOrLease = isTenderOrDirectOrLease

            if (!this.token && !isTenderOrDirectOrLease) {
                return
            }

            try {
                const {default: MonetizationService} = await import('../MyAccount/components/Monetization/MonetizationService')
                const response = await MonetizationService.getRequestStatus(this.monetizationId)
                this.isOfferValidityExpired = false
                this.monetizationStatus = response
                /**
                 * for tender or directSale is not necessary to log in
                 */
                this.needLoginForJoin = !(this.monetizationStatus?.type === 'tender' || this.monetizationStatus?.type === 'secretTender' || this.monetizationStatus?.type === 'directSale')

                if (this.monetizationStatus.offerValidity) {
                    const now = this.moment()
                    if (now.isAfter(this.moment(this.monetizationStatus.offerValidity?.date))) {
                        this.isOfferValidityExpired = true
                    }
                }
            } catch (e) {
                console.error('Cannot fetch request status', e)
            }
        },

        async getTours() {
            try {
                const {default: MonetizationService} = await import('../MyAccount/components/Monetization/MonetizationService')
                console.log(MonetizationService)
                const data = await MonetizationService.searchTours(this.monetizationId, 0, 20)
                this.tours = data && data.records || []
            } catch (e) {
                console.error('Cannot fetch tours', e)
            }
        },

        setAccessToken(token) {
            if (token) {
                localStorage.setItem('accessToken', token)
                this.token = token
            }
        },

        openLoginDialog() {
            this.$modal.show('login-dialog')
        },

        cancel() {
            this.$modal.hide('login-dialog')
        },

        async enterToAuction() {
            this.showMonetizationComponent = true
            await sleep(100)
            Event.fire('monetizationDialogOpen', {
                monetizationId: this.monetizationId,
                previewMode: true,
                isLogged: this.isLogged,
                state: this.monetizationId,
                type: this.monetizationStatus.type
            })
        },

        async joinToAuction() {
            this.showMonetizationComponent = true
            await sleep(100)
            Event.fire('monetizationDialogOpen', {
                monetizationId: this.monetizationId,
                previewMode: false,
                isLogged: this.isLogged,
                state: this.monetizationId,
                type: this.monetizationStatus.type
            })
        },

        async showMonetizationDialogInPreviewMode() {
            this.showMonetizationComponent = true
            await sleep(100)
            Event.fire('monetizationDialogOpen', {
                monetizationId: this.monetizationId,
                previewMode: true,
                isLogged: this.isLogged,
                state: this.monetizationId,
                type: this.monetizationStatus.type
            })
        },

        async joinToTour(tour) {
            if (this.token) {
                try {
                    const body = {contact: this.account.id}
                    const {default: axios} = await import('axios')
                    await axios.post(`${API_URL}/tour/${tour.id}/join`, body, {headers: {'content-type': 'application/json'}})
                    tour.status = 'joined'
                    const {SuccessModal} = await import(/* webpackPrefetch: true */'../Modals/SuccessModal')
                    SuccessModal.show(this, this.$i18n.t('monetizationDetail.joinedToTour.success'))
                } catch (e) {
                    const {ErrorModal} = await import(/* webpackPrefetch: true */'../Modals/ErrorModal')
                    if (e.response.data?.errors?.code === 1701) {
                        ErrorModal.show(this, this.$i18n.t('error.1701'))
                    } else {
                        ErrorModal.show(this, this.$i18n.t(e.response.data['error'] ? `error.${e.response.data['error']}` : 'error.somethingWrong'))
                    }
                }
            } else {
                this.showJoinToTourDialogComponent = true
                Event.fire('joinToTourDialogOpen', {tour: tour || null})
            }
        },

        async getAccount() {
            try {
                const {default: axios} = await import('axios')
                const response = await axios.get(`${API_URL}/account`, {headers: {'content-type': 'application/json'}})
                return response && response.data && response.data.record || null
            } catch (e) {
                console.error('Cannot fetch account', e)
            }
        },

        async fetchPublicLog() {
            try {
                const {default: axios} = await import('axios')
                const {data} = await axios.post(`${API_URL}/monetization/${this.monetizationId}/publicLog`, {page: 0, perPage: 9999}, {headers: {'content-type': 'application/json'}})
                this.logRecords = data.records
            } catch (e) {
                console.error('Cannot fetch publicLog', e)
            }
        },

        async connectToWS() {
            const {default: axios} = await import('axios')
            const {data: {publicWebSocketUrl}} = await axios.get(`${API_URL}/core`, {headers: {'content-type': 'application/json'}})
            try {
                const conn = new ab.Session(`${publicWebSocketUrl}`,
                    async () => {
                        console.info('WebSocket connection established')
                        conn.subscribe(`monetization_${this.monetizationId}`, (topic, data) => {
                            console.info('ws:notifications', data);
                            if (data) {
                                this.logRecords = data.log
                            }
                        });
                    },
                    () => {
                        console.warn('WebSocket connection closed');
                    },
                    {skipSubprotocolCheck: true}
                );
            } catch (e) {
                console.error(e)
            }
        },

        getDate(log) {
            return this.moment(log.date).format('DD.MM.YYYY HH:mm:ss.SS')
        },

        getWho(log) {
            if (typeof log.who === 'string') {
                return log.who === 'robot' ? 'Licitátor' : log.who
            } else {
                return log.who.contact.code
            }
        },

        getType(log) {
            return log.text.includes('online-auction') ? 'dražby' : 'aukce'
        },

        getParticipant(log) {
            if (log.text.includes('@')) {
                const participantCodes = log.text.split('@')
                participantCodes.shift()
                return participantCodes.length === 2 ? participantCodes.join(' a ') : participantCodes.join(', ')
            }
            return ''
        },

        getParticipantText(log) {
            if (log.text.includes('@')) {
                const participantCodes = log.text.split('@')
                participantCodes.shift()
                return participantCodes.length > 1 ? 'účastníky' : 'účastníkem'
            }
            return ''
        },

        getParticipantTextBid(log) {
            if (log.text.includes('@')) {
                const participantCodes = log.text.split('@')
                participantCodes.shift()
                return participantCodes.length > 1 ? 'udělím jim příklep' : 'udělím mu příklep'
            }
            return ''
        },

        getParticipantLotText(log) {
            if (log.text.includes('@')) {
                const participantCodes = log.text.split('@')
                participantCodes.shift()
                return participantCodes.length > 1 ? 'Probíhá losování z přihlášených účastníků s předkupním právem.' : ''
            }
            return ''
        },

        getLogText(log) {
            let htmlText = log.htmlText
            if (htmlText) {
              if (log.who?.contact?.id === this.accountId) {
                htmlText = htmlText.replace(`<strong>${log.who.contact.code}</strong>`, `<strong>${log.who.contact.code}</strong> <div class="icon text-primary" style="position: relative; top: -2px;"><svg aria-hidden="true" data-prefix="fas" data-icon="user" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="svg-inline--fa fa-user fa-w-14 fa-3x"><path fill="currentColor" d="M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z"></path></svg></div>`)
              }
              return htmlText
            }
            return ''
        },

        truncateLog(text) {
            return truncateFilter(text, 500, true)
        },

        logCollapse(log, val) {
            log['collapsed'] = val
            this.$forceUpdate()
        },
        
        slice(val, countChars) {
            return val ? val.slice(countChars) : val
        },

        setAccountFullName() {
            if (this.account) {
                const name = this.account.type === 'legal' ? this.account.company : `${this.account.name} ${this.account.surname}`
                localStorage.setItem('accountFullName', name)
                window.dispatchEvent(new CustomEvent('accountFullName-localstorage-changed', {
                    detail: {
                      storage: localStorage.getItem('accountFullName')
                    }
                }));
            }
        }
    }
});
